import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Database, FileSpreadsheet, Globe2, Layers } from "lucide-react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { getMapeamentoOrigemDados } from "../../api";
import SideBarNavOrigemDados from "../../componets/SideBarNavOrigemDados";

// Mesmo mapeamento de cor usado nos badges da tela de Mapeamento —
// mantém as duas telas visualmente consistentes.
const ORIGEM_CORES = {
  "Google Sheets": "#10B981",
  "Banco de Dados": "#3B82F6",
  "API Externa": "#F59E0B",
};
const ORIGEM_ICONES = {
  "Google Sheets": FileSpreadsheet,
  "Banco de Dados": Database,
  "API Externa": Globe2,
};
const COR_PADRAO = "#94A3B8";

function KpiCard({ icon: Icon, label, value, pct, cor }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
      <div className="p-3 rounded-xl" style={{ background: `${cor}1A` }}>
        <Icon className="w-5 h-5" style={{ color: cor }} />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-800">
          {value}
          {pct != null && <span className="text-sm font-medium text-gray-400 ml-1.5">({pct}%)</span>}
        </p>
      </div>
    </div>
  );
}

export default function OrigemDadosDashboard() {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErro(null);
      try {
        setDados(await getMapeamentoOrigemDados());
      } catch (e) {
        setErro(e.message || "Acesso não autorizado.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const itens = useMemo(() => dados?.itens ?? [], [dados]);

  const { origens, totalItens, distribuicao } = useMemo(() => {
    const contagem = new Map();
    for (const item of itens) {
      const chave = item.origem_dados || "Outros";
      contagem.set(chave, (contagem.get(chave) || 0) + 1);
    }
    const total = itens.length;
    const dist = Array.from(contagem.entries()).map(([origem, qtd]) => ({
      origem,
      qtd,
      pct: total ? Math.round((qtd / total) * 1000) / 10 : 0,
    }));
    return { origens: contagem, totalItens: total, distribuicao: dist };
  }, [itens]);

  const porModulo = useMemo(() => {
    const mapa = new Map();
    for (const item of itens) {
      const modulo = item.modulo || "Outros";
      if (!mapa.has(modulo)) mapa.set(modulo, { modulo, "Google Sheets": 0, "Banco de Dados": 0, "API Externa": 0 });
      const linha = mapa.get(modulo);
      const chave = item.origem_dados || "Outros";
      linha[chave] = (linha[chave] || 0) + 1;
    }
    return Array.from(mapa.values());
  }, [itens]);

  const alturaGraficoModulos = Math.max(240, porModulo.length * 34);

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>
      <SideBarNavOrigemDados isLight />

      <div className="flex flex-col flex-1 bg-gray-50" style={{ overflow: "auto" }}>
        <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center shadow-md">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin")} className="p-1.5 rounded-lg hover:bg-white/10 transition">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="text-lg font-bold tracking-tight">Painel Admin · Dashboard de Origem dos Dados</span>
          </div>
          <button
            onClick={() => navigate("/inicio")}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600,
            }}
          >
            Menu Principal
          </button>
        </header>

        <main className="flex-1 px-6 py-8 max-w-6xl mx-auto w-full">
          {loading && <div className="py-16 text-center text-gray-400 text-sm">Carregando dados…</div>}
          {!loading && erro && <div className="py-16 text-center text-red-500 text-sm">{erro}</div>}

          {!loading && !erro && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <KpiCard icon={Layers} label="Total mapeado" value={totalItens} cor="#7C3AED" />
                {["Google Sheets", "Banco de Dados", "API Externa"].map((origem) => (
                  <KpiCard
                    key={origem}
                    icon={ORIGEM_ICONES[origem]}
                    label={origem}
                    value={origens.get(origem) || 0}
                    pct={totalItens ? Math.round(((origens.get(origem) || 0) / totalItens) * 100) : 0}
                    cor={ORIGEM_CORES[origem]}
                  />
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-2">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">Distribuição por origem</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Proporção de funcionalidades do dashboard por tipo de fonte de dados.
                  </p>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={distribuicao}
                        dataKey="qtd"
                        nameKey="origem"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={2}
                      >
                        {distribuicao.map((d) => (
                          <Cell key={d.origem} fill={ORIGEM_CORES[d.origem] || COR_PADRAO} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(valor, _nome, item) => [`${valor} (${item.payload.pct}%)`, item.payload.origem]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-3 justify-center mt-2">
                    {distribuicao.map((d) => (
                      <div key={d.origem} className="flex items-center gap-1.5 text-xs text-gray-600">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: ORIGEM_CORES[d.origem] || COR_PADRAO }} />
                        {d.origem}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-3">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">Origem por módulo / tela</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Quantas funcionalidades de cada tela vêm de planilha, banco de dados ou API externa.
                  </p>
                  <ResponsiveContainer width="100%" height={alturaGraficoModulos}>
                    <BarChart data={porModulo} layout="vertical" margin={{ left: 12, right: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F1F5F9" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} />
                      <YAxis
                        type="category"
                        dataKey="modulo"
                        width={170}
                        tick={{ fontSize: 11, fill: "#475569" }}
                      />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Google Sheets" stackId="origem" fill={ORIGEM_CORES["Google Sheets"]} />
                      <Bar dataKey="Banco de Dados" stackId="origem" fill={ORIGEM_CORES["Banco de Dados"]} />
                      <Bar dataKey="API Externa" stackId="origem" fill={ORIGEM_CORES["API Externa"]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </main>

        <footer className="text-center text-xs text-gray-400 py-6">
          © {new Date().getFullYear()} Youx Group · by financeiro
        </footer>
      </div>
    </div>
  );
}
